import { memo } from 'react'
import { safeSheetOpenHaptic } from '../../lib/safeTelegram'

/**
 * Figma Menu Component — нижнее меню навигации
 *
 * Props:
 * - element: "none" | "home" | "earn" | "profile" (default: "home")
 * - onChange: обработчик смены вкладки (tab: string) => void
 * - className: дополнительные классы
 *
 * Размеры из Figma:
 * - Размер: 250×64px
 * - Border radius: 999px (pill)
 * - Position: fixed bottom-7, centered
 * - Glass effect + backdrop-blur
 * - Индикаторы: SVG-свечение за активной иконкой (192×192)
 */

const TABS = [
  { id: 'home', label: 'Главная' },
  { id: 'earn', label: 'Заработать' },
  { id: 'profile', label: 'Профиль' },
]

const ICONS = {
  home: {
    active: '/images/icons/figma/nav-home-active.svg',
    inactive: '/images/icons/figma/nav-home-inactive.svg'
  },
  earn: {
    active: '/images/icons/figma/nav-earn-active.svg',
    inactive: '/images/icons/figma/nav-earn-inactive.svg'
  },
  profile: {
    active: '/images/icons/figma/nav-profile-active.svg',
    inactive: '/images/icons/figma/nav-profile-inactive.svg'
  },
}

const INDICATORS = {
  home: '/images/icons/figma/nav-indicator-home.svg',
  earn: '/images/icons/figma/nav-indicator-earn.svg',
  profile: '/images/icons/figma/nav-indicator-profile.svg',
}

function Menu({ element = 'home', onChange, className = '' }) {
  if (element === 'none') {
    return null
  }

  const handleTabClick = (tabId) => {
    safeSheetOpenHaptic()
    onChange?.(tabId)
  }

  return (
    <nav
      className={`
        fixed bottom-[calc(1.75rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2
        z-40
        flex items-center gap-3
        w-[250px] h-16
        px-4 py-2
        rounded-full
        border border-[rgba(250,250,255,0.1)]
        backdrop-blur-[6px]
        overflow-visible
        ${className}
      `}
      style={{
        background: 'linear-gradient(90deg, rgba(250, 250, 255, 0.05) 0%, rgba(250, 250, 255, 0.05) 100%), linear-gradient(90deg, rgba(8, 8, 10, 0.9) 0%, rgba(8, 8, 10, 0.9) 100%)',
      }}
      aria-label="Навигация"
    >
      {TABS.map(({ id, label }) => {
        const isActive = element === id

        return (
          <button
            key={id}
            type="button"
            onClick={() => handleTabClick(id)}
            className={`
              relative flex h-12 w-16 shrink-0
              flex-col items-center justify-center gap-1
              text-[12px] font-[510] leading-[14px] tracking-[-0.01em]
              transition-colors duration-200
              ${isActive ? 'text-[#007CFF]' : 'text-[rgba(250,250,255,0.5)]'}
            `}
            aria-current={isActive ? 'page' : undefined}
          >
            {/* Icon */}
            <img loading="eager"
              src={ICONS[id][isActive ? 'active' : 'inactive']}
              alt=""
              width="28"
              height="28"
              className="relative z-[1] h-7 w-7"
              aria-hidden="true"
            />

            {/* Label */}
            <span className="relative z-[1] whitespace-nowrap">{label}</span>

            {/* Indicator — glow за активной иконкой */}
            {isActive && (
              <span
                className="pointer-events-none absolute left-1/2 top-[4px] z-0 h-16 w-16 -translate-x-1/2"
                aria-hidden="true"
              >
                <img loading="eager"
                  src={INDICATORS[id]}
                  alt=""
                  width="64"
                  height="64"
                  className="h-full w-full"
                />
              </span>
            )}
          </button>
        )
      })}
    </nav>
  )
}

export default memo(Menu)
